import { createContext, useEffect, useState, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";

// Create the AuthContext
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => {
    return useContext(AuthContext);
};

// AuthProvider component to manage authentication state
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // Stores the decoded user data
    const [isLoading, setIsLoading] = useState(true); // Added loading state
    const navigate = useNavigate();
    const timerRef = useRef(null); // Timer reference for auto-logout

    // Logout function
    const logout = useCallback(() => {
        localStorage.removeItem("token"); // Remove token from localStorage
        localStorage.removeItem("name"); // Remove token from localStorage
        setUser(null); // Clear user data
        navigate("/login"); // Redirect to login page
        if (timerRef.current) {
            clearTimeout(timerRef.current); // Clear the auto-logout timer
        }
    }, [navigate]);

    // Reset the auto-logout timer
    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current); // Clear existing timer
        }
        timerRef.current = setTimeout(logout, 86400000); // 24 hours in milliseconds
    }, [logout]);

    // Check for an existing token on app load
    useEffect(() => {
        const token = localStorage.getItem("token");

        if (token) {
            try {
                const decoded = jwtDecode(token); // Decode the token

                if (decoded.exp * 1000 < Date.now()) {
                    // Token has expired
                    logout();
                } else {
                    setUser(decoded); // Set the user data
                    resetTimer(); // Start the auto-logout timer
                }
            } catch (error) {
                console.error("Invalid token", error);
                logout(); // Log out if the token is invalid
            }
        }

        setIsLoading(false); // Mark loading as complete after token check
    }, [logout, resetTimer]);

    // Login function
    const login = useCallback((token, data) => {
        localStorage.setItem("token", token); // Save the token to localStorage
        localStorage.setItem("name", `${data.firstName} ${data.lastName}`); // Save the token to localStorage
        const decoded = jwtDecode(token); // Decode the token
        setUser(decoded); // Set the user data
        resetTimer(); // Start the auto-logout timer
        navigate("/"); // Redirect to the dashboard or home page
    }, [navigate, resetTimer]);

    // Listen for user activity (clicks and key presses)
    useEffect(() => {
        if (!user) return; // Only listen for activity if the user is logged in

        const handleUserActivity = () => {
            resetTimer(); // Reset the timer whenever user activity is detected
        };

        // Add event listeners for user activity
        window.addEventListener("click", handleUserActivity);
        window.addEventListener("keydown", handleUserActivity);

        // Cleanup event listeners on unmount
        return () => {
            window.removeEventListener("click", handleUserActivity);
            window.removeEventListener("keydown", handleUserActivity);
        };
    }, [user, resetTimer]); // Add `user` and `resetTimer` as dependencies

    // Provide the authentication state and functions to the context
    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;